import { ApiError } from '@eneco/dc/client/ApiError';
import { postCustomerDiscontinueIntakeSecure } from '@eneco/dc/services';
import { MachineConfig } from '@eneco/flows/types';
import {
  DC_Customers_Client_Models_CustomerDiscontinueIntakeCustomerSecureResponseModel,
  ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeV2ResponseModel,
} from '@monorepo-types/dc';
import { assign, fromPromise, setup } from 'xstate';

import { FlowContextDeceased } from '../../context';

interface DeceasedCustomerMachineContext extends FlowContextDeceased {
  errorMessage?: string;
  status?: 'open' | 'expired' | 'accepted' | 'denied';
  deceasedCustomerInformation?: DC_Customers_Client_Models_CustomerDiscontinueIntakeCustomerSecureResponseModel;
}
interface InputType {
  event: GetCustomerEvent;
  context: DeceasedCustomerMachineContext;
}

interface OutputType {
  intakeData: ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeV2ResponseModel;
}

export interface DeceasedCustomer {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  customerId?: number;
  street?: string;
}
type SearchEvent =
  | {
      type: 'GET_CUSTOMER';
      config: MachineConfig;
      values: DeceasedCustomer;
    }
  | { type: 'RESET' };

type GetCustomerEvent = Extract<SearchEvent, { type: 'GET_CUSTOMER' }>;

const postIntakeService = (
  config: MachineConfig,
  customerObject: DeceasedCustomer,
): Promise<ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeV2ResponseModel> => {
  return new Promise((resolve, reject) => {
    postCustomerDiscontinueIntakeSecure({
      businessUnit: config.businessUnit,
      label: config.label,
      customerId: customerObject.customerId || 0,
      requestBody: {
        data: {
          postalCode: customerObject.postalCode,
          houseNumber: customerObject.houseNumber,
          houseNumberSuffix:
            customerObject.houseNumberSuffix && customerObject.houseNumberSuffix.length > 0
              ? customerObject.houseNumberSuffix
              : null,
        },
      },
    })
      .then(response => {
        resolve(response);
      })
      .catch((error: ApiError) => {
        reject(new Error(error.message));
      });
  });
};

const getValuesFromAttributes = (
  context: DeceasedCustomerMachineContext,
  intakeResponse: ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeV2ResponseModel,
): Omit<FlowContextDeceased, 'basketId'> => {
  if (intakeResponse?.data?.customer) {
    context.deceasedCustomerInformation = intakeResponse.data.customer;
  }
  return context;
};

export const searchMachine = setup({
  types: {} as {
    context: DeceasedCustomerMachineContext;
    events: SearchEvent;
    actors: {
      getData: {
        input: InputType;
        output: OutputType;
      };
    };
  },
  actions: {
    clearErrorMessage: assign({ errorMessage: undefined }),
  },
  actors: {
    getData: fromPromise<OutputType, InputType>(({ input }) => {
      const { event } = input;
      if (event.type !== 'GET_CUSTOMER') {
        throw new Error('Invalid event type');
      }
      return postIntakeService(event.config, event.values).then(intakeData => ({ intakeData }));
    }),
  },
}).createMachine({
  id: 'search-machine',
  initial: 'IDLE',
  context: {
    errorMessage: undefined,
    deceasedCustomerInformation: undefined,
  },
  states: {
    IDLE: {
      entry: 'clearErrorMessage',
      on: {
        GET_CUSTOMER: { target: 'FETCHING' },
      },
    },
    FETCHING: {
      invoke: {
        src: 'getData',
        input: ({ event, context }) => {
          if (event.type !== 'GET_CUSTOMER') {
            throw new Error('Invalid event type for getData actor');
          }
          return { event, context };
        },
        onDone: {
          target: 'SUCCESS',
          actions: [
            assign(({ context, event }) => {
              const { intakeData } = event.output;
              return {
                ...getValuesFromAttributes(context, intakeData),
              };
            }),
          ],
        },
        onError: {
          target: 'ERROR',
          actions: assign({
            errorMessage: ({ event }) => (event.error as Error).message,
          }),
        },
      },
    },
    SUCCESS: {
      type: 'final',
      entry: 'clearErrorMessage',
    },
    ERROR: {
      on: { RESET: { target: 'IDLE' } },
    },
  },
});
